import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useDrop } from "react-dnd";
import { Document, Page, pdfjs } from "react-pdf";
import DraggableResizableField from "./DraggableResizableField";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const PAGE_GAP = 10;
const VIRTUALIZATION_BUFFER = 500; // px above/below viewport to pre-render

const PdfDropZone = ({
  pdfUrl,
  editFields,
  signFields,
  addField,
  editorMode,
  handleFieldDelete,
  handleFieldResize,
  handleUpdateFields,
  setPdfDimensions,
  pdfDimensions,
}) => {
  const pdfContainerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [pdfScale, setPdfScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [droppedField, setDroppedField] = useState(null);
  const [visiblePages, setVisiblePages] = useState(new Set([1]));
  const [pageHeight, setPageHeight] = useState(1100);

  // Calculate which pages are visible based on scroll position
  const updateVisiblePages = useCallback(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl || numPages === 0 || pageHeight === 0) return;

    const scrollTop = scrollEl.scrollTop;
    const viewportHeight = scrollEl.clientHeight;
    const totalPageHeight = pageHeight + PAGE_GAP;

    const firstVisible = Math.max(
      1,
      Math.floor((scrollTop - VIRTUALIZATION_BUFFER) / totalPageHeight) + 1
    );
    const lastVisible = Math.min(
      numPages,
      Math.ceil((scrollTop + viewportHeight + VIRTUALIZATION_BUFFER) / totalPageHeight)
    );

    const newVisible = new Set();
    for (let i = firstVisible; i <= lastVisible; i++) {
      newVisible.add(i);
    }

    setVisiblePages((prev) => {
      if (prev.size === newVisible.size && [...prev].every((p) => newVisible.has(p))) {
        return prev;
      }
      return newVisible;
    });
  }, [numPages, pageHeight]);

  // Listen to scroll events
  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return;

    const handleScroll = () => {
      requestAnimationFrame(updateVisiblePages);
    };

    scrollEl.addEventListener("scroll", handleScroll, { passive: true });
    updateVisiblePages();

    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, [updateVisiblePages]);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "FIELD",
      drop: (item, monitor) => {
        const offset = monitor.getSourceClientOffset();
        if (offset && pdfContainerRef.current) {
          const rect = pdfContainerRef.current.getBoundingClientRect();
          const x = offset.x - rect.left;
          const y = offset.y - rect.top;

          const totalPageHeight = pageHeight + PAGE_GAP;
          const page = Math.min(
            numPages,
            Math.floor(y / totalPageHeight) + 1
          );
          let width = 200;
          let height = 100;
          if (item.type.toUpperCase() === "TEXT") {
            width = 200;
            height = 40;
          }
          setDroppedField({
            type: item.type,
            name: item.name || item.type,
            width: width,
            height: height,
            top: y - totalPageHeight * (page - 1),
            left: x,
            page: page,
            target: editorMode,
          });
        }
      },
      collect: (monitor) => ({ isOver: !!monitor.isOver() }),
    }),
    [editorMode, numPages, pageHeight]
  );

  // Apply the dropped field after rendering
  useEffect(() => {
    if (droppedField) {
      addField(
        droppedField.type,
        droppedField.name,
        droppedField.top,
        droppedField.left,
        droppedField.width,
        droppedField.height,
        droppedField.page,
        droppedField.target
      );
      setDroppedField(null);
    }
  }, [droppedField, addField]);

  const onDocumentLoad = useCallback(
    async ({ numPages }) => {
      setNumPages(numPages);

      if (!pdfUrl) return;

      try {
        const loadingTask = pdfjs.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;

        if (!pdf) {
          console.error("PDF document is null");
          return;
        }

        const firstPage = await pdf.getPage(1);

        if (!firstPage) {
          console.error("First page is null");
          return;
        }

        const viewport = firstPage.getViewport({ scale: 2 });
        const { width, height } = viewport;

        setPdfDimensions({ width, height });
        setContainerWidth(width);
        setPageHeight(height);
      } catch (error) {
        console.error("Error getting PDF page dimensions:", error);
      }
    },
    [setPdfDimensions, pdfUrl]
  );

  // Memoize page array to avoid re-creating on every render
  const pageNumbers = useMemo(
    () => Array.from({ length: numPages }, (_, i) => i + 1),
    [numPages]
  );

  return (
    <div
      ref={(node) => {
        drop(node);
        scrollContainerRef.current = node;
      }}
      style={{
        flex: 1,
        overflow: "auto",
        position: "relative",
        width: `${containerWidth}px`,
        margin: "0 auto",
        backgroundColor: "#f0f0f0",
      }}
    >
      <div ref={pdfContainerRef} style={{ position: "relative" }}>
        <Document file={pdfUrl} onLoadSuccess={onDocumentLoad}>
          {pageNumbers.map((pageNumber) => {
            const isVisible = visiblePages.has(pageNumber);
            return (
              <div
                key={pageNumber}
                style={{
                  position: "relative",
                  width: `${containerWidth}px`,
                  height: `${pageHeight}px`,
                  marginBottom: `${PAGE_GAP}px`,
                }}
              >
                {isVisible ? (
                  <Page
                    pageNumber={pageNumber}
                    width={containerWidth}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#e0e0e0",
                    }}
                  />
                )}

                {/* Render sign fields only if in sign mode */}
                {editorMode === "sign" &&
                  signFields
                    .filter((field) => field.page === pageNumber)
                    .map((field) => (
                      <DraggableResizableField
                        key={`sign-${field.id}`}
                        field={field}
                        onDrag={(x, y) =>
                          handleUpdateFields(
                            field.id,
                            field.type,
                            field.name,
                            y,
                            x,
                            field.width,
                            field.height,
                            pageNumber,
                            "sign"
                          )
                        }
                        onResize={(width, height) =>
                          handleFieldResize(field.id, width, height, "sign")
                        }
                        pdfScale={pdfScale}
                        onDelete={() => handleFieldDelete(field.id, "sign")}
                      />
                    ))}

                {/* Render edit fields only if in edit mode */}
                {editorMode === "edit" &&
                  editFields
                    .filter((field) => field.page === pageNumber)
                    .map((field) => (
                      <DraggableResizableField
                        key={`edit-${field.id}`}
                        field={field}
                        onDrag={(x, y) =>
                          handleUpdateFields(
                            field.id,
                            field.type,
                            field.name,
                            y,
                            x,
                            field.width,
                            field.height,
                            pageNumber,
                            "edit"
                          )
                        }
                        onResize={(width, height) =>
                          handleFieldResize(field.id, width, height, "edit")
                        }
                        pdfScale={pdfScale}
                        onDelete={() => handleFieldDelete(field.id, "edit")}
                        onTextEdit={(text) => {
                          handleUpdateFields(
                            field.id,
                            field.type,
                            text,
                            field.top,
                            field.left,
                            field.width,
                            field.height,
                            pageNumber,
                            "edit"
                          );
                        }}
                        editorMode="edit"
                      />
                    ))}
              </div>
            );
          })}
        </Document>
      </div>
    </div>
  );
};

export default PdfDropZone;
